import React, { useState, useEffect } from "react";
import { Heart, Loader2 } from "lucide-react";
import { createCheckout } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

const PRESETS = [5, 10, 25, 50];

const Donate = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [amount, setAmount] = useState(10);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) setForm((f) => ({ name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  const chosen = custom ? parseFloat(custom) : amount;

  const pickPreset = (v) => { setAmount(v); setCustom(""); };

  const donate = async () => {
    const value = custom ? parseFloat(custom) : amount;
    if (!value || value < 1) {
      toast({ title: "Please enter at least $1", variant: "destructive" });
      return;
    }
    if (!form.name || !form.email) {
      toast({ title: "Please add your name and email", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { checkout_url } = await createCheckout({
        items: [],
        donation: Math.round(value * 100) / 100,
        customer: { ...form, phone: "", note: "Donation" },
        origin_url: window.location.origin,
      });
      window.location.href = checkout_url;
    } catch (err) {
      setLoading(false);
      toast({ title: "Couldn't start donation", description: err?.response?.data?.detail || "Please try again.", variant: "destructive" });
    }
  };

  return (
    <main className="max-w-[1000px] mx-auto px-6 mt-10 min-h-[60vh] fade-up">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-[#0e2a1e] px-8 md:px-14 py-14 text-center">
        <div className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(circle_at_50%_20%,#a4d61e_0%,transparent_55%)]" />
        <div className="relative">
          <div className="w-14 h-14 rounded-full bg-[#a4d61e] flex items-center justify-center mx-auto">
            <Heart size={26} className="text-[#0e2a1e]" />
          </div>
          <h1 className="font-serif-brand text-4xl md:text-5xl font-semibold text-[#f4f2ea] mt-6">Support Everyone's Budget</h1>
          <p className="text-[#f4f2ea]/80 mt-5 max-w-2xl mx-auto leading-relaxed">
            Everyone's Budget was built on a simple belief — that high quality shouldn't come with a high price, and that a small local shop can make a real difference for the community around it. Your donation helps us keep our prices low, expand the range of quality essentials we offer, and continually improve the experience for every neighbour who shops with us — faster pickups, friendlier service, and more support for the Mississauga families we're proud to serve. However much you choose to give, you're helping a local business keep giving back, and for that we're truly grateful.
          </p>
        </div>
      </div>

      {/* Donation form */}
      <div className="max-w-xl mx-auto mt-10 bg-white rounded-2xl border border-[#0e2a1e]/8 p-7">
        <h2 className="font-serif-brand text-2xl font-semibold text-[#0e2a1e] text-center">Choose an amount</h2>

        <div className="grid grid-cols-4 gap-3 mt-6">
          {PRESETS.map((v) => (
            <button
              key={v}
              onClick={() => pickPreset(v)}
              className={`py-3 rounded-xl font-semibold text-sm border transition-colors ${!custom && amount === v ? "bg-[#0e2a1e] text-[#f4f2ea] border-[#0e2a1e]" : "bg-[#f4f2ea] text-[#0e2a1e] border-[#0e2a1e]/15 hover:border-[#0e2a1e]/40"}`}
            >
              ${v}
            </button>
          ))}
        </div>

        <div className="mt-4">
          <label className="text-sm font-medium text-[#0e2a1e]">Or enter a custom amount</label>
          <div className="mt-1.5 flex items-center bg-[#f4f2ea] border border-[#0e2a1e]/15 rounded-md px-4 focus-within:border-[#0e2a1e]/40">
            <span className="text-[#0e2a1e]/60">$</span>
            <input
              type="number" min="1" step="1" value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="0.00"
              className="flex-1 bg-transparent py-2.5 px-2 text-sm outline-none"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name*" className="bg-[#f4f2ea] border border-[#0e2a1e]/15 rounded-md px-4 py-2.5 text-sm outline-none focus:border-[#0e2a1e]/40" />
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email*" className="bg-[#f4f2ea] border border-[#0e2a1e]/15 rounded-md px-4 py-2.5 text-sm outline-none focus:border-[#0e2a1e]/40" />
        </div>

        <button
          onClick={donate}
          disabled={loading}
          className="w-full mt-6 bg-[#a4d61e] text-[#0e2a1e] font-semibold py-3.5 rounded-full hover:bg-[#b6e63a] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading ? (<><Loader2 size={18} className="animate-spin" /> Redirecting…</>) : (<><Heart size={18} /> Donate ${(chosen || 0).toFixed(2)}</>)}
        </button>
        <p className="text-xs text-[#0e2a1e]/50 text-center mt-3">Secure one-time payment via Stripe · CAD</p>
      </div>
    </main>
  );
};

export default Donate;
